import { dispatch, state } from '__common/store';
import { isBlankMap } from '__common/constants/map';
import { showWarningAlert } from '__common/modules/alerts';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { SET_PROJECT_OPTION } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import {
  backCalculateRoofPitchFromPanelSetbacks,
  CORRUPTED_FLAT_ROOF_PITCH,
  hasCorruptedFlatRoofPitch,
  hasUnconditionalSubNineSlopeCap,
  maxSelectableRoofPitch,
  RoofPitchLimits,
  toPitchNumber,
} from './roofPitchFit';

export type RoofPitchRecoveryMethod = 'panel-setback-fit' | 'clamped-to-product-maximum';

export interface RoofPitchRecovery {
  pitch: number;
  method: RoofPitchRecoveryMethod;
}

const roofPitchLimits = (projectEnvConfig: any, area: string): RoofPitchLimits => ({
  buildingCode: projectEnvConfig.building_code,
  area,
  flashLocRmMechanicalAttachments: Boolean(projectEnvConfig.allow_mechanical_attachments)
    && projectEnvConfig.attachment_type === AttachmentType.UNIRAC_FLASHLOC_RM,
});

export const recoverCorruptedRoofPitch = (
  roof: any,
  productId: number,
  projectEnvConfig: any,
  area: string,
  mapType: string,
): RoofPitchRecovery | null => {
  const limits = roofPitchLimits(projectEnvConfig, area);
  // On a blank map the roof polygon comes from virtual roof edges, which the
  // slope never scales, so there is nothing to invert.
  const backCalculated = isBlankMap(mapType)
    ? null
    : backCalculateRoofPitchFromPanelSetbacks(roof, productId, Number(projectEnvConfig.tilt) || 0, limits);

  if (backCalculated !== null) {
    return { pitch: backCalculated, method: 'panel-setback-fit' };
  }
  // Without a fit to go on, clamping is only safe where the selector could
  // never have offered 9 in the first place. For the products where it could,
  // the stored value has to be left alone rather than guessed at.
  if (!hasUnconditionalSubNineSlopeCap(productId)) {
    return null;
  }
  return {
    pitch: maxSelectableRoofPitch(productId, limits),
    method: 'clamped-to-product-maximum',
  };
};

const CLAMPED_PITCH_WARNING = 'The roof slope saved with this project is not one this product allows,'
  + ' and it could not be recovered from the layout. It has been set to the steepest allowed slope -'
  + ' please confirm the roof slope before running the project.';

/**
 * Repairs the roof slope of a single roof in place, if it is corrupted. Called
 * while the project is being loaded, before anything reads the slope, so the
 * repaired value flows into the panels editor, the BOM and the next save.
 *
 * `roof.roofPitch` is already a string by this point (see getRoofs), so the
 * repaired value is written back in the same shape.
 */
export const repairCorruptedRoofPitch = (roof: any, productId: number, mapType: string): RoofPitchRecovery | null => {
  if (!hasCorruptedFlatRoofPitch(productId, roof && roof.roofPitch)) {
    return null;
  }

  const { projectConfiguration: { projectEnvConfig, area } } = state();
  const recovery = recoverCorruptedRoofPitch(roof, productId, projectEnvConfig, area, mapType);
  if (recovery === null) {
    return null;
  }
  roof.roofPitch = String(recovery.pitch);

  if (toPitchNumber(projectEnvConfig.roof_pitch) === CORRUPTED_FLAT_ROOF_PITCH) {
    dispatch(SET_PROJECT_OPTION('roof_pitch', recovery.pitch));
  }

  if (recovery.method === 'clamped-to-product-maximum') {
    showWarningAlert(CLAMPED_PITCH_WARNING);
  }

  return recovery;
};
